import { Controller } from '@hotwired/stimulus'
import { Turbo } from '@hotwired/turbo-rails'

// Buscador de la comparativa de ETFs (ENH-RNK-044, `_comparar_buscador.haml`), en la página vacía y encima de la
// tabla: sugiere mientras se escribe, sin pulsar «Buscar». Cada búsqueda pide el
// turbo_stream de `comparar`, que cambia solo las sugerencias: ni la tabla ni el campo se repintan, y el campo
// no pierde el foco. Sin JS sigue siendo un formulario GET con su botón.
//
// Enter lleva al primer resultado; las flechas recorren la lista y Esc la cierra y vuelve al campo.
const ESPERA_AL_ESCRIBIR = 200
const MINIMO_DE_LETRAS = 2
// Lo que se deja libre entre la lista desplegada y el borde de la ventana.
const MARGEN = 16
const DERECHA = 'rnk-Comparativa_Resultados-derecha'

export default class extends Controller {
  static targets = ['campo', 'boton', 'resultados', 'opcion']

  connect () {
    this.botonTarget.hidden = true
    // Lo que ya trae la página (`?q=`) son las sugerencias de lo que hay escrito.
    this.pintado = this.consulta()
    this.cerrarFuera = this.cerrarFuera.bind(this)
    document.addEventListener('click', this.cerrarFuera)
    // El stream cambia la lista cuando Turbo lo aplica, no al recibirlo: se abre al ver el cambio.
    this.observador = new MutationObserver(() => this.abrir())
    this.observador.observe(this.resultadosTarget, { childList: true })
  }

  disconnect () {
    clearTimeout(this.espera)
    this.peticion?.abort()
    this.observador.disconnect()
    document.removeEventListener('click', this.cerrarFuera)
  }

  escribir () {
    clearTimeout(this.espera)
    if (this.consulta().length < MINIMO_DE_LETRAS) {
      this.peticion?.abort()
      this.pintado = null
      this.cerrar()
      return
    }

    this.espera = setTimeout(() => this.buscar(), ESPERA_AL_ESCRIBIR)
  }

  // Enter: al primer resultado, si la lista es la de lo escrito. Si aún no ha llegado, el formulario se
  // envía como sin JS y la página trae las sugerencias.
  enviar (event) {
    const primera = this.opcionTargets[0]
    if (!primera || this.pintado !== this.consulta()) return

    event.preventDefault()
    primera.click()
  }

  teclaEnCampo (event) {
    if (event.key === 'ArrowDown' && this.abierto()) {
      event.preventDefault()
      this.opcionTargets[0]?.focus()
    } else if (event.key === 'Escape' && this.abierto()) {
      event.preventDefault()
      this.cerrar()
    }
  }

  teclaEnLista (event) {
    const posicion = this.opcionTargets.indexOf(document.activeElement)
    if (posicion < 0) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      this.opcionTargets[Math.min(posicion + 1, this.opcionTargets.length - 1)].focus()
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      ;(this.opcionTargets[posicion - 1] || this.campoTarget).focus()
    } else if (event.key === 'Escape') {
      // Primero el foco: al volver al campo, la lista se reabre.
      event.preventDefault()
      this.campoTarget.focus()
      this.cerrar()
    }
  }

  // Una petición viva a la vez: al escribir rápido, la respuesta de una búsqueda vieja no puede pisar a la
  // nueva. Si algo falla se queda lo que había: el botón de siempre vuelve a estar a mano.
  async buscar () {
    const consulta = this.consulta()
    const formulario = this.element.querySelector('form')
    const url = new URL(formulario.action, window.location.origin)
    // Con lo que lleve el formulario: al cambiar un ETF, el hueco (`cambiar`).
    new FormData(formulario).forEach((valor, nombre) => url.searchParams.set(nombre, valor))
    url.searchParams.set('q', consulta)
    this.peticion?.abort()
    const peticion = new AbortController()
    this.peticion = peticion
    this.resultadosTarget.setAttribute('aria-busy', 'true')
    try {
      const cabeceras = { Accept: 'text/vnd.turbo-stream.html' }
      const respuesta = await fetch(url, { headers: cabeceras, signal: peticion.signal })
      if (!respuesta.ok) throw new Error(`HTTP ${respuesta.status}`)

      const stream = await respuesta.text()
      this.pintado = consulta
      Turbo.renderStreamMessage(stream)
    } catch (error) {
      if (error.name !== 'AbortError') this.botonTarget.hidden = false
    } finally {
      if (this.peticion === peticion) this.resultadosTarget.removeAttribute('aria-busy')
    }
  }

  // La lista es más ancha que el campo: sale alineada a su izquierda y, si por ese lado no cabe en la ventana,
  // a su derecha (y al revés). En móvil ocupa el ancho del campo.
  // Mientras llega la búsqueda de lo último que se ha escrito se queda la anterior, en vez de parpadear.
  abrir () {
    const lista = this.resultadosTarget
    lista.hidden = lista.children.length === 0 || this.consulta().length < MINIMO_DE_LETRAS
    if (lista.hidden) return

    const { left, right } = lista.getBoundingClientRect()
    if (lista.classList.contains(DERECHA) ? left < MARGEN : right > document.documentElement.clientWidth - MARGEN) {
      lista.classList.toggle(DERECHA)
    }
  }

  cerrar () {
    this.resultadosTarget.hidden = true
  }

  cerrarFuera (event) {
    if (!this.element.contains(event.target)) this.cerrar()
  }

  abierto () {
    return !this.resultadosTarget.hidden
  }

  consulta () {
    return this.campoTarget.value.trim()
  }
}
